import React, { useRef, useState } from "react";

import {
  useAuth,
  useDefaultCountry,
  useOperator,
} from "../../../globals/hooks";
import authApi from "utils/auth/authApi";
import InternationalPhoneInput from "components/globals/InternationalPhoneInput";
import { Box, Button, TextField } from "@mui/material";

const AUTO_LOGIN_COOLDOWN_MS = 60 * 1000;

type AutoLoginAttemptState = {
  inFlight: boolean;
  lastAttemptedValue: string | null;
  blockedUntil: number;
};

const createDefaultAttemptState = (): AutoLoginAttemptState => ({
  inFlight: false,
  lastAttemptedValue: null,
  blockedUntil: 0,
});

const shouldSkipAutoLoginAttempt = (
  state: AutoLoginAttemptState,
  currentValue: string,
) => {
  const now = Date.now();

  return (
    state.inFlight ||
    (state.lastAttemptedValue === currentValue && now < state.blockedUntil)
  );
};

function MobileAndEmailAuthentication() {
  // state
  const [mobilePhoneInput, setMobilePhoneInput] = useState({
    mobilePhone: "",
    phoneCountryCode: "",
    phoneCountryDialCode: "",
    phoneCountryName: "",
    phoneCountryFormat: "",
  });
  const [email, setEmail] = useState(null);

  // hooks
  const { operator } = useOperator();
  const { onLoginDialogOpen } = useAuth();
  const defaultCountry = useDefaultCountry();
  const phoneAutoLoginRef = useRef<AutoLoginAttemptState>(
    createDefaultAttemptState(),
  );
  const emailAutoLoginRef = useRef<AutoLoginAttemptState>(
    createDefaultAttemptState(),
  );

  // event handlers
  const handleMobilePhoneChange = (value, country) => {
    setMobilePhoneInput({
      mobilePhone: value,
      phoneCountryCode: country.countryCode,
      phoneCountryDialCode: country.dialCode,
      phoneCountryName: country.name,
      phoneCountryFormat: country.format,
    });
  };

  const handleEmailChange = (event) => {
    const { value } = event.target;
    setEmail(value);
  };

  const findExistingUserWithMobile = async () => {
    const mobilePhone = mobilePhoneInput.mobilePhone.trim();
    const attemptState = phoneAutoLoginRef.current;

    // if phone number is not appropriate length, do not make api call
    // some international numbers can be 8 digits (solomon islands)
    if (mobilePhone.length < 8) {
      return;
    }
    if (shouldSkipAutoLoginAttempt(attemptState, mobilePhone)) return;

    attemptState.inFlight = true;

    try {
      // check to see if existing contact
      const { status } = await authApi.sendCode({
        mobilePhone: mobilePhoneInput,
        email: null,
        operatorSlug: operator.nameSlug,
      });

      attemptState.lastAttemptedValue = mobilePhone;
      attemptState.blockedUntil = Date.now() + AUTO_LOGIN_COOLDOWN_MS;

      // code was successfully sent, meaning a user exists
      if (status === 200) {
        onLoginDialogOpen({
          mobilePhone: mobilePhoneInput,
          verifyCodeText: "We found an account with your mobile number",
          verifyButtonText: "Login",
        });
      }
    } finally {
      attemptState.inFlight = false;
    }
  };

  const findExistingUserWithEmail = async () => {
    const normalizedEmail = email?.trim().toLowerCase();
    const attemptState = emailAutoLoginRef.current;

    if (!normalizedEmail) return;
    if (shouldSkipAutoLoginAttempt(attemptState, normalizedEmail)) return;

    attemptState.inFlight = true;

    try {
      const { status } = await authApi.sendCode({
        email: normalizedEmail,
        mobilePhone: null,
        operatorSlug: operator.nameSlug,
      });

      attemptState.lastAttemptedValue = normalizedEmail;
      attemptState.blockedUntil = Date.now() + AUTO_LOGIN_COOLDOWN_MS;

      if (status === 200) {
        onLoginDialogOpen({
          email: normalizedEmail,
          verifyButtonText: "Login",
          verifyCodeText: "We found an account with your email",
        });
      }
    } finally {
      attemptState.inFlight = false;
    }
  };

  return (
    <>
      <InternationalPhoneInput
        value={mobilePhoneInput.mobilePhone}
        onChange={handleMobilePhoneChange}
        onBlur={findExistingUserWithMobile}
        label={"Mobile Phone Number"}
        defaultCountry={defaultCountry}
      />

      <Box mt={1}>
        <Button
          variant="outlined"
          fullWidth
          onClick={findExistingUserWithMobile}
          disabled={mobilePhoneInput.mobilePhone.length < 8}
        >
          Login with Phone
        </Button>
      </Box>

      <Box mt={1} mb={2}>
        <TextField
          fullWidth
          name="email"
          variant="outlined"
          placeholder="Email address"
          label="Email address"
          onChange={handleEmailChange}
          onBlur={findExistingUserWithEmail}
          value={email}
        />
      </Box>
      <Button
        variant="outlined"
        fullWidth
        onClick={findExistingUserWithEmail}
        disabled={!email}
      >
        Login with Email
      </Button>
    </>
  );
}

export default MobileAndEmailAuthentication;
